import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import RequestBreadcrumb from '../../../common/RequestBreadcrumb'
import { TemplateEntityTypesEnum, TemplateService, TemplateTypeEnum, LeaseAgreementService, LeaseAgreementTemplateSectionVM, LeaseAgreementDataAttributesEnum } from '@propertelligent/client-api'
import { UserLoginData } from '../../../common/charts/Enums'
import QuestionHeader from '../QuestionHeader'
import "../AddTemplate/AddTemplate.css"
import { useNavigate, useParams, useLocation } from 'react-router'
import apiHelper from '../../../../utils/apiHelper'
import AddTraspButton from '../../../firstTimeUserExperience/commonCom/AddTraspButton'
import AddButtonWithArrow from '../../../firstTimeUserExperience/commonCom/AddButtonWithArrow'
import { GetImages } from '../../../../utils/GetImages'
import CustomInputs from '../../../common/Inputs'
import { FORM_CONTROLFIELD_TYPES } from '../../../../constants/constants'
import "./LeaseTemplate.css"
import CustomEditor from './CustomEditor'
import EmptyState from '../../../../newComponents/emptystate'
import ShowLeaseAgreement from '../../../tenants/LeaseAgreement/ShowLeaseAgreement'
import DataAttributePanel from './DataAttributePanel/DataAttributePanel'
import { TextCss } from '../../../GlobalCss/TextCss'
import LeftArrowTopHeader from '../../../../newComponents/form-fields/LeftArrowTopHeader'
import SeparaterLine from '../../../common/SeparaterLine'
import { usePermissions } from "../../../../hooks/usePermissions";
import { PermissionGuard } from "../../../../newComponents/PermissionGuard";
import NoAccess from "../../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { isDeepEqual } from '../../../../utils/helper';

const AddLeaseAgreementTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editorRef = useRef([]);
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [previewApplication, setPreviewApplication] = useState(false);
  const [editSection, setEditSection] = useState<Number>();
  const [initialData, setInitialData] = useState<any | null>(null);
  const [postData, setPostData] = useState<any | undefined>(
    {
      name: "New Lease Agreement",
      description: "Lease agreement for prospective tenants",
      templateTypeId: TemplateTypeEnum.LeaseAgreement,
      entityTypeId: TemplateEntityTypesEnum.Organization,
      entityId: parseInt(sessionStorage.getItem(UserLoginData.organization_id)),
      isInUse: false,
      leaseAgreementTemplateSections: [] as LeaseAgreementTemplateSectionVM,
    })
  const [dataAttribute, setdataAttribute] = useState<any>([]);
  const hasInitialized = useRef(false);

  const check = usePermissions();
  const canWrite = check.canWrite("Templates");

  const AddSection = () => {
    const newSection = { sectionName: `Section ${postData.leaseAgreementTemplateSections.length + 1}`, sectionData: "" };
    setPostData(prevData => ({
      ...prevData,
      leaseAgreementTemplateSections: [...prevData.leaseAgreementTemplateSections, newSection]
    }));
  }

  const copySection = useCallback(
    (index) => {
      const sectionToCopy = { ...postData.leaseAgreementTemplateSections[index] };
      sectionToCopy.sectionName = `${sectionToCopy.sectionName} (Copy)`;
      setPostData((prevData) => ({
        ...prevData,
        leaseAgreementTemplateSections: [
          ...prevData.leaseAgreementTemplateSections,
          sectionToCopy,
        ],
      }));
    },
    [postData]
  );

  const deleteSection = useCallback(
    (index) => {
      setPostData((prevData) => ({
        ...prevData,
        leaseAgreementTemplateSections: prevData.leaseAgreementTemplateSections.filter((_, idx) => idx !== index),
      }));
    },
    [postData]
  );

  useEffect(() => getDataAttributesData(), []);

  useEffect(() => { if (id) { getTempleteData() } }, [id, editorRef]);

  const toggleSection = (index) => {
    setPostData(prevPostData => ({
      ...prevPostData,
      leaseAgreementTemplateSections: prevPostData.leaseAgreementTemplateSections?.map((section, idx) =>
        idx === index ? { ...section, isOpen: !section.isOpen } : section
      ),
    }));
  };

  const toggleAllSections = () => {
    setPostData(prevPostData => {
      const areAllExpanded = prevPostData.leaseAgreementTemplateSections?.every(section => section.isOpen);
      return {
        ...prevPostData,
        leaseAgreementTemplateSections: prevPostData.leaseAgreementTemplateSections?.map(section => ({
          ...section,
          isOpen: !areAllExpanded,
        })),
      };
    });
  };

  const handleSectionChange = useCallback((id, key, value) => {
    setPostData(prevData => {
      const updatedSections = prevData.leaseAgreementTemplateSections?.map((section, idx) =>
        idx === id ? { ...section, [key]: value } : section
      );
      return {
        ...prevData,
        leaseAgreementTemplateSections: updatedSections,
      };
    });
  }, [postData]);


  const getIconForId = (id: number) => {

    const enumKey = Object.keys(LeaseAgreementDataAttributesEnum).find(key => LeaseAgreementDataAttributesEnum[key as keyof typeof LeaseAgreementDataAttributesEnum] === id);

    if (!enumKey) return GetImages.selectedTenantIconMenu;

    if (/(Date|Day|Period)/i.test(enumKey)) {
      return GetImages.AttributeCalendar;
    }
    if (/(Rent|Fee)/i.test(enumKey)) {
      return GetImages.DollerBlue;
    }
    if (/(Lessor|Lessee)/i.test(enumKey)) {
      return GetImages.BlueUser;
    }
    if (/Address/i.test(enumKey)) {
      return GetImages.BluePin;
    }
    if (/LeaseType/i.test(enumKey)) {
      return GetImages.BlueFile;
    }
    if (/Deposit/i.test(enumKey)) {
      return GetImages.SecurityDepositIcon;
    }

    return GetImages.selectedTenantIconMenu;
  };

  const enrichedData = dataAttribute.map(item => ({
    ...item,
    icon: getIconForId(item.id),
    placeholder: `[ ${Object.keys(LeaseAgreementDataAttributesEnum).find(key => LeaseAgreementDataAttributesEnum[key as keyof typeof LeaseAgreementDataAttributesEnum] === item.id
    )} ]`,
  }));


  const buttonClose = () => { navigate("/organization/templates"); }

  const getTempleteData = () => {
    apiHelper(
      TemplateService.getApiTemplate,
      { showNotification: false },
      Number(id)
    ).then((res) => {
      if (res?.data?.success) {
        const templateData = res.data.success;
        setPostData(templateData);
        setInitialData(templateData);
      }
    }).catch((err) => { console.error("Error fetching template data:", err); });
  };

  const isDirty = useMemo(() => {
    if (!id) return true;
    if (!initialData) return false;
    if (postData?.name !== initialData?.name) return true;
    if (postData?.description !== initialData?.description) return true;

    const normalizeSections = (sections: any[]) => {
      if (!Array.isArray(sections)) return [];
      return sections.map((sec) => ({
        sectionName: sec?.sectionName ?? "",
        sectionData: sec?.sectionData ?? "",
      }));
    };

    const currentSections = normalizeSections(postData?.leaseAgreementTemplateSections);
    const initialSections = normalizeSections(initialData?.leaseAgreementTemplateSections);

    return !isDeepEqual(currentSections, initialSections);
  }, [id, initialData, postData]);

  const getDataAttributesData = () => {
    apiHelper(
      LeaseAgreementService.getApiLeaseAgreementLeaseAgreementDataAttributes, { showNotification: false }).then((res) => {
        if (res?.data?.success) {
          const DataAttributesData = res.data.success;
          setdataAttribute(DataAttributesData);
        }
      }).catch((err) => { console.error("Error fetching template data:", err); });
  };

  const handleSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      const apiCall = id
        ? apiHelper(TemplateService.putApiTemplate, {
          title: "Template Updated",
          successMessage: "Template edited Successfully",
          failureMessage: "Unable to Add Template",
          showNotification: true,
        }, Number(id), postData)
        : apiHelper(TemplateService.postApiTemplate, {
          title: "Template Saved",
          successMessage: "Template saved succussfully",
          failureMessage: "Unable to updated Template",
          showNotification: true,
        }, postData)
      const res = await apiCall;
      if (res?.data?.success) { navigate("/organization/templates", { state: { activeTab: location.state?.fromTemplateTab || "LeaseAgreement" } }); setIsLoading(false); }
    } catch (error) { setIsLoading(false); } finally { setIsLoading(false); }
  }, [id, postData, navigate]);

  const duplicateTemplete = () => {
    if (id) {
      apiHelper(TemplateService.postApiTemplateDuplicate, {
        title: "Template Duplicate",
        successMessage: "Template Duplicated Succussfully",
        failureMessage: "Unable to Duplicate Template",
        showNotification: true,
      }, Number(id)).then((res) => {
        if (res?.data?.success) { navigate(`/organization/templates/lease-agreement/${res?.data?.success.id}`); }
      }).catch((err) => { });
    }
  };


  const deleteTemplete = () => {
    if (id) {
      apiHelper(TemplateService.deleteApiTemplate, {
        title: "Template Deleted",
        successMessage: "Template deleted successfully",
        failureMessage: "Unable to Delete Template",
        showNotification: true,
      }, Number(id))
        .then((res) => {
          navigate("/organization/templates", { state: { activeTab: location.state?.fromTemplateTab || "LeaseAgreement" } });
        })
        .catch((err) => { });
    }
  };

  const buttonConfigs = [
    {
      imageValue: GetImages.BlackPencil,
      onClick: (index, event) => {
        event.stopPropagation();
        setEditSection(index)
      },
      IconClassName: "iconstyle16 color-white-icon"
    },
    {
      imageValue: GetImages.BlackCopy,
      onClick: (index, event) => {
        event.stopPropagation();
        copySection(index)
      },
      IconClassName: "iconstyle16 color-white-icon"
    },
    {
      imageValue: GetImages.deleteIconRed,
      onClick: (index, event) => {
        event.stopPropagation();
        deleteSection(index)
      },
      IconClassName: "iconstyle16"
    },

    {
      imageValue: GetImages.BlackChevronDown,
      onClick: (index, event) => {
        event.stopPropagation();
        toggleSection(index)
      },
      dynamicClass: (isOpen) => (isOpen ? 'chevron-rotated' : ''),
      IconClassName: "iconstyle24"
    },
  ];

  const activeConfigs = canWrite ? buttonConfigs : [buttonConfigs[3]];

  useEffect(() => {
    if (!postData?.leaseAgreementTemplateSections?.length) return;

    if (!hasInitialized.current) {
      hasInitialized.current = true;
      const isAnySectionOpen = postData.leaseAgreementTemplateSections.some(section => section.isOpen);

      if (!isAnySectionOpen) {
        setPostData(prev => ({
          ...prev,
          leaseAgreementTemplateSections: prev?.leaseAgreementTemplateSections?.map((section, index) => ({
            ...section,
            isOpen: index === 0,
          })),
        }));
      }
    }
  }, [postData?.leaseAgreementTemplateSections]);
  const attributeGroups = [
    {
      groupName: "Basics",
      icon: GetImages.BlackFile,
      fields: ["Tenant Name", "Landlord Name", "Unit Address", "Lease Type", "Lessee Name", "Lessor Name", "Lessor Addresss"]
    },
    {
      groupName: "Amounts",
      icon: GetImages.newPaymentIcon,
      fields: [
        "Additional Fee", "Late Fee", "Late Rent Interest", "Parking Fee",
        "Pet Fee", "Rent", "Storage Fee", "Storage Security Deposit",
        "Pet Security Deposit", "Parking Security Deposit", "Security Deposit",
        "Security Deposit Interest"
      ],
      classNames: "iconstyle24"
    },
    {
      groupName: "Date",
      icon: GetImages.calenderIcon,
      fields: ["Due Day", "Start Date", "End Date", "Grace Period", "Notice Period"]
    }
  ];

  const groupedAttributes = attributeGroups.map(group => ({
    ...group,
    items: enrichedData.filter(attr => group.fields.includes(attr.name))
  }));


  return (
    <PermissionGuard permission="Templates" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
      <div className="application-section">
        {!previewApplication ?
          <>

            <QuestionHeader postData={postData} setPostData={setPostData} duplicateTemplete={duplicateTemplete} deleteTemplete={deleteTemplete} handleSubmit={handleSubmit} />
            <div className='lease-agreement-section'>
              {canWrite && (
                <div className="lease-agreement-section-right">
                  <div className='data-attribute px-3 py-2'>
                    <p className={`${TextCss.black.heading_600_16}`}>Data Attributes</p>
                    <p className={TextCss.sub_heading_400_14}>Drag & drop below attribute to add in the agreement.</p>
                  </div>
                  <div className='lease-agreement-section-right-menu-main'>
                    <DataAttributePanel groupedAttributes={groupedAttributes} />
                  </div>
                </div>
              )}
              <div className={`lease-agreement-section-left ${!canWrite ? 'w-100' : ''}`}>
                <div className='lease-agreement-section-left-header'>
                  <div className='d-flex justify-content-between pb-2'>
                    <p className={TextCss.black.main_heading_700_20}>Lease Agreement Sections</p>
                    {postData?.leaseAgreementTemplateSections?.length > 0 &&
                      <AddTraspButton
                        width={0}
                        bname={
                          postData.leaseAgreementTemplateSections?.every((section) => section.isOpen)
                            ? "Collapse All"
                            : "Expand All"
                        }
                        imageValue={GetImages?.BlackChevronDown}
                        iconPosition={"right"}
                        onClick={toggleAllSections}
                        border={false}
                        IconClassName={postData.leaseAgreementTemplateSections?.every((section) => section.isOpen)
                          ? "chevron-rotated iconstyle24"
                          : "iconstyle24"}
                        className={"sub_text fw-600 black gap-2 varsha"}
                      />
                    }
                  </div>
                  {postData?.leaseAgreementTemplateSections?.length !== 0 ?
                    <>
                      <div className='lease-agreement-section-left-item'>
                        {Array.isArray(postData?.leaseAgreementTemplateSections) && postData?.leaseAgreementTemplateSections?.map((section, index) => (
                          <div key={index} className="lease-agreement-accordion">
                            <div className="lease-agreement-accordion-header"
                              onClick={() => toggleSection(index)}
                            >
                              {(!section?.isOpen || editSection !== index) ?
                                <p className='sub_heading black'>{section?.sectionName}</p>
                                :
                                <> {(editSection == index && section?.isOpen) && (
                                  <div onClick={(e) => e.stopPropagation()} className='lease-agreement-accordion-header-input-wrap'>
                                    <CustomInputs
                                      name={undefined}
                                      type={FORM_CONTROLFIELD_TYPES.TEXT}
                                      value={section?.sectionName}
                                      labelText={undefined}
                                      isFormControl
                                      maxLength={100}
                                      onChange={(e) => handleSectionChange(index, "sectionName", e.target.value)}
                                      onBlur={() => setEditSection(null)}
                                      containerClassName="w-100  "
                                      placeholder="Add section name"
                                      id="customInputBorder"
                                      myClassName="m-0 border-0 outline-0 sub_heading black"
                                      isError={undefined}
                                      errorMessages={undefined}
                                    />
                                  </div>
                                )}
                                </>}
                              <div className='flexRow'>
                                <div className="options_buttons">
                                  {activeConfigs.map((button, i) => (
                                    (section?.isOpen || button.imageValue === GetImages.BlackChevronDown) && (
                                      <React.Fragment key={i}>
                                        <AddTraspButton
                                          key={i}
                                          width={0}
                                          bname={undefined}
                                          imageValue={button?.imageValue}
                                          iconPosition={"right"}
                                          onClick={(event) => button?.onClick(index, event)}
                                          IconClassName={button.IconClassName}
                                          border={false}
                                          className={button.dynamicClass ? button.dynamicClass(section.isOpen) : ''}
                                        />
                                        {i === 2 && canWrite && (<SeparaterLine height={16} background="grey" />)}
                                      </React.Fragment>
                                    )
                                  ))}
                                </div>
                              </div>
                            </div>

                            {section?.isOpen && (
                              <div className="lease-agreement-accordion-content" style={{ padding: "10px" }}  >
                                <CustomEditor
                                  enrichedData={enrichedData}
                                  section={section?.sectionData}
                                  setPostData={setPostData}
                                  postData={postData}
                                  index={index} />
                              </div>)}
                          </div>
                        ))}
                        {canWrite && (
                          <AddTraspButton
                            bname={"Add Section"}
                            iconPosition={"right"}
                            imageValue={undefined}
                            onClick={AddSection}
                            border={true} />
                        )}
                      </div>
                    </>
                    : <EmptyState heading={"No Section Available"} actionNode={canWrite ? <AddTraspButton bname={"Section"} iconPosition={"right"}
                      imageValue={GetImages.BlueAdd} onClick={AddSection} border={true} /> : null} isBorder={false} />
                  }
                </div>
              </div>

            </div>

            <div className="application-section-bttom-view1">
              <AddTraspButton type="button" bname={"Cancel"} onClick={buttonClose} border={false} width="fit-content" />
              {canWrite && <AddButtonWithArrow buttonname={isLoading ? "Saving..." : "Preview"} onClick={() => setPreviewApplication(true)} minWidth="fit-content" />}
            </div>
          </>
          :
          <div className='leaseAgreement'>
            <div className="application-top-card">
              <LeftArrowTopHeader
                className={TextCss.black.heading_700_16}
                title={`${postData?.name}`}
                onCancel={() => setPreviewApplication(false)}
              />

            </div>

            <div className='leaseAgreementSave'>
              <h6 className={TextCss.black.heading_700_16}>Lease Agreement Preview</h6>
              <div className=' borderWithBorderRadious leaseAgreementPreview'>
                <ShowLeaseAgreement
                  postLeaseAgreement={undefined}
                  setPostLeaseAgreement={undefined}
                  onCancel={() => setPreviewApplication(false)}
                  onClick={handleSubmit}
                  buttonName={"Save"}
                  role="tenant"
                  selectedTemplateid={undefined}
                  userData={undefined}
                  selectedTemplates={postData}
                  isLoading={isLoading} leaseApplicationId={undefined} />
              </div>

            </div>
            <div className="application-section-bttom-view1">
              <AddTraspButton
                type="button"
                bname={"Cancel"} onClick={() => setPreviewApplication(false)} border={false} width="fit-content" />
              {canWrite && (
                <AddButtonWithArrow
                  buttonname={isLoading ? "Saving..." : "Save Agreement"}
                  onClick={handleSubmit}
                  disabled={isLoading || !isDirty}
                  minWidth="fit-content"
                />
              )}
            </div>
          </div>
        }
      </div>
    </PermissionGuard>
  )
}

export default AddLeaseAgreementTemplate

